import {
  AttemptStatusType,
  ProblemDifficultyType,
  stringToAttemptStatusType,
  stringToProblemDifficultyType,
} from "../../../config/enums";

import {TableRowDataInterface} from "../../../components/table/TableBodyUI";
import {TableContentViewModel} from "../../../components/table/TableUI";
import {
  TableCellInterface,
  TableCellViewType,
} from "../../../components/table/TableCellUI";
import {stringToRichText} from "../../../config/view-models/RichText";
import {RichTextViewModel} from "../../../config/view-models/RichText";
import {ChipViewModel} from "../../../config/view-models/Chip";
import {statusTheme} from "../../../config/themes/status-theme";

export type GetQuestionsResponseType = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: {
    title: string;
    tags: {reference_id: string; name: string}[];
    reference_id: string;
    slug: string;
    question_type: string;
    difficulty: string;
    is_suggested?: boolean;
    is_must_do?: boolean;
    is_faq?: boolean;
    status: string;
    time_taken: string;
  }[];
};

function timeToString(time: number): string {
  if (time === 0) return "";
  if (time >= 3600) return "1hr+";
  const mins = Math.floor(time / 60) > 0 ? `${Math.floor(time / 60)}m ` : "";
  const secs = time % 60 > 0 ? `${time % 60}s` : "";
  return `${mins}${secs}`;
}

export const parseGetQuestionListResponse = (
  response: GetQuestionsResponseType,
): Pick<TableContentViewModel, "rowsData" | "pagination"> => {
  const rows = response.results.map((problem): TableRowDataInterface => {
    const tags: ChipViewModel[] = problem.tags.map(tag => ({
      label: stringToRichText(tag.name, {fontSize: "0.75rem", fontWeight: 400}),
    }));
    if (problem.is_suggested) {
      tags.push({
        label: stringToRichText("Suggested", {
          fontSize: "0.75rem",
          fontWeight: 600,
        }),
        icon: "auto_awesome",
        style: {backgroundColor: "success.main", color: "white"},
      });
    }
    if (problem.is_must_do) {
      tags.push({
        label: stringToRichText("Must Do", {
          fontSize: "0.75rem",
          fontWeight: 600,
        }),
        icon: "play_arrow",
        style: {backgroundColor: "warning.main", color: "white"},
      });
    }
    if (problem.is_faq) {
      tags.push({
        label: stringToRichText("Trending", {
          fontSize: "0.75rem",
          fontWeight: 600,
        }),
        icon: "trending_up",
        style: {backgroundColor: "#9c27b0", color: "white"},
      });
    }

    return {
      id: problem.slug,
      data: [
        getStatusIcon(problem.status),
        {
          cellType: TableCellViewType.RICH_TEXT_WITH_TAGS,
          label: stringToRichText(problem.title, {
            fontSize: "0.875rem",
            fontWeight: 400,
          }),
          tags,
        },
        {
          cellType: TableCellViewType.RICH_TEXT,
          label: getDifficultyRichText(problem.difficulty),
        },
        {
          cellType: TableCellViewType.RICH_TEXT,
          label: stringToRichText(timeToString(Number(problem.time_taken)), {
            fontSize: "0.875rem",
            fontWeight: 400,
          }),
        },
      ],
    };
  });

  return {
    rowsData: rows,
    pagination: {
      next: response.next ?? null,
      previous: response.previous ?? null,
    },
  };
};

function getStatusIcon(status: string): TableCellInterface {
  const statusEnum = stringToAttemptStatusType(status);
  switch (statusEnum) {
    case AttemptStatusType.Unseen:
      return {
        cellType: TableCellViewType.EMPTY,
      };
    case AttemptStatusType.Seen:
      return {
        cellType: TableCellViewType.ICON,
        icon: "pause",
        color: statusTheme[statusEnum],
      };
    case AttemptStatusType.Correct:
      return {
        cellType: TableCellViewType.ICON,
        icon: "check",
        color: statusTheme[statusEnum],
      };
    case AttemptStatusType.Incorrect:
      return {
        cellType: TableCellViewType.ICON,
        icon: "close",
        color: statusTheme[statusEnum],
      };
    default:
      return {
        cellType: TableCellViewType.EMPTY,
      };
  }
}

function getDifficultyRichText(difficulty: string): RichTextViewModel {
  const difficultyEnum = stringToProblemDifficultyType(difficulty);
  const fontStyle = {fontSize: "0.875rem", fontWeight: 500};
  switch (difficultyEnum) {
    case ProblemDifficultyType.Easy:
      return stringToRichText("Easy", {color: "success.main", ...fontStyle});
    case ProblemDifficultyType.Medium:
      return stringToRichText("Medium", {color: "warning.main", ...fontStyle});
    case ProblemDifficultyType.Hard:
      return stringToRichText("Hard", {color: "error.main", ...fontStyle});
    default:
      return stringToRichText("");
  }
}
